/**
 * Authentication Context
 *
 * Manages Firebase authentication state and user session.
 *
 * Security Features:
 * - Backend verification after Firebase auth (user must exist in DB)
 * - Access Denied state for unauthorized users
 * - Automatic token refresh handling
 * - Clean logout (clears all state)
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { getFirebaseAuth, googleProvider } from '../services/firebase';
import { api } from '../services/api';
import { config } from '../config/environment';
import type { User, Client, UserRole } from '../types';

// Mock data for development
const mockUser: User = {
  id: 'mock-user-id',
  email: 'test@example.com',
  firstName: 'Test',
  lastName: 'User',
  clientId: 'mock-client-id',
  role: 'admin',
  createdAt: new Date().toISOString(),
};

const mockClient: Client = {
  id: 'mock-client-id',
  name: 'Test Client',
  color: '#0D6433',
  logo: '',
  createdAt: new Date().toISOString(),
};

interface AuthContextType {
  // State
  user: User | null;
  client: Client | null;
  firebaseUser: FirebaseUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessDenied: boolean; // User exists in Firebase but not in our DB

  // Role-based access
  role: UserRole | null;
  isSuperAdmin: boolean;
  isAdmin: boolean;       // Client admin only
  isViewer: boolean;
  isAnyAdmin: boolean;    // super_admin OR admin

  // Impersonation
  impersonatedUser: User | null;
  impersonatedClient: Client | null;
  isImpersonating: boolean;
  startImpersonation: (user: User, client: Client | null, returnPath?: string) => void;
  stopImpersonation: () => string | null; // Returns the path to navigate to

  // Effective values (real or impersonated)
  effectiveUser: User | null;
  effectiveClient: Client | null;
  effectiveRole: UserRole | null;

  // Actions
  login: () => Promise<void>;
  logout: () => Promise<void>;

  // Error handling
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Firebase user (from Firebase Auth)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);

  // App user (from our database)
  const [user, setUser] = useState<User | null>(null);
  const [client, setClient] = useState<Client | null>(null);

  // UI states
  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Impersonation state
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [impersonatedClient, setImpersonatedClient] = useState<Client | null>(null);
  const [impersonationReturnPath, setImpersonationReturnPath] = useState<string | null>(null);

  // Derived state - role-based access
  const role = user?.role ?? null;
  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role === 'admin';
  const isViewer = role === 'viewer';
  const isAnyAdmin = isSuperAdmin || isAdmin;

  // Impersonation derived state
  const isImpersonating = impersonatedUser !== null;
  const effectiveUser = impersonatedUser ?? user;
  const effectiveClient = impersonatedClient ?? client;
  const effectiveRole = impersonatedUser?.role ?? role;

  // User is authenticated if they exist in DB
  // super_admin may not have a client, so we only require user
  const isAuthenticated = !!user && (isSuperAdmin || !!client);

  /**
   * Clear all auth state
   */
  const clearAuthState = useCallback(() => {
    setUser(null);
    setClient(null);
    setFirebaseUser(null);
    setAccessDenied(false);
    setError(null);
    setImpersonatedUser(null);
    setImpersonatedClient(null);
    setImpersonationReturnPath(null);
  }, []);

  /**
   * Start impersonating a user
   * - Super Admin can impersonate anyone
   * - Admin can only impersonate viewers in their client
   * - returnPath: optional path to return to when stopping impersonation
   */
  const startImpersonation = useCallback((targetUser: User, targetClient: Client | null, returnPath?: string) => {
    // Validation: Only admins can impersonate
    if (!isAnyAdmin) {
      console.warn('Only admins can impersonate users');
      return;
    }

    // Validation: Admin can only impersonate viewers in their client
    if (isAdmin && !isSuperAdmin) {
      if (targetUser.role !== 'viewer' || targetUser.clientId !== user?.clientId) {
        console.warn('Admin can only impersonate viewers in their own client');
        return;
      }
    }

    setImpersonatedUser(targetUser);
    setImpersonatedClient(targetClient);
    setImpersonationReturnPath(returnPath || null);
  }, [isAnyAdmin, isAdmin, isSuperAdmin, user?.clientId]);

  /**
   * Stop impersonating and return to real user
   * Returns the path to navigate to (if one was saved)
   */
  const stopImpersonation = useCallback((): string | null => {
    const returnPath = impersonationReturnPath;
    setImpersonatedUser(null);
    setImpersonatedClient(null);
    setImpersonationReturnPath(null);
    return returnPath;
  }, [impersonationReturnPath]);

  /**
   * Verify user exists in database after Firebase auth
   * SECURITY: This is the critical step that ensures only registered users can access
   */
  const verifyUserInDatabase = useCallback(async (fbUser: FirebaseUser): Promise<boolean> => {
    try {
      const response = await api.auth.me();

      if (response.error) {
        // User not found in database - Access Denied
        console.warn('User not found in database:', fbUser.email);
        setAccessDenied(true);
        setError('Access denied. Your account is not registered in the system.');

        // Sign out from Firebase to clear state
        const auth = getFirebaseAuth();
        await signOut(auth);

        return false;
      }

      if (response.data) {
        setUser(response.data.user);
        setClient(response.data.client);
        setAccessDenied(false);
        return true;
      }

      return false;
    } catch (err) {
      console.error('Failed to verify user:', err);
      setError('Failed to verify user. Please try again.');
      return false;
    }
  }, []);

  /**
   * Handle Firebase auth state changes
   * Skip in mock mode
   */
  useEffect(() => {
    // Skip Firebase auth listener in mock mode
    if (config.features.useMock) {
      setIsLoading(false);
      return;
    }

    const auth = getFirebaseAuth();

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setIsLoading(true);

      if (fbUser) {
        setFirebaseUser(fbUser);

        // Verify user exists in our database
        await verifyUserInDatabase(fbUser);
      } else {
        // Not logged in
        clearAuthState();
      }

      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [verifyUserInDatabase, clearAuthState]);

  /**
   * Listen for unauthorized events from API
   */
  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn('Received unauthorized event, logging out');
      logout();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  /**
   * Login with Google (or mock in development)
   */
  const login = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setAccessDenied(false);

    // Mock mode - skip Firebase entirely
    if (config.features.useMock) {
      console.log('Mock mode: Logging in with mock user');
      setUser(mockUser);
      setClient(mockClient);
      setIsLoading(false);
      return;
    }

    try {
      const auth = getFirebaseAuth();
      await signInWithPopup(auth, googleProvider);
      // onAuthStateChanged will handle the rest
    } catch (err) {
      console.error('Login failed:', err);

      // Handle specific Firebase errors
      if (err instanceof Error) {
        if (err.message.includes('popup-closed-by-user')) {
          setError('Login cancelled');
        } else if (err.message.includes('popup-blocked')) {
          setError('Popup blocked. Please allow popups for this site.');
        } else {
          setError('Login failed. Please try again.');
        }
      }

      setIsLoading(false);
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      const auth = getFirebaseAuth();
      await signOut(auth);
      clearAuthState();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }, [clearAuthState]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextType = {
    user,
    client,
    firebaseUser,
    isAuthenticated,
    isLoading,
    accessDenied,
    role,
    isSuperAdmin,
    isAdmin,
    isViewer,
    isAnyAdmin,
    // Impersonation
    impersonatedUser,
    impersonatedClient,
    isImpersonating,
    startImpersonation,
    stopImpersonation,
    effectiveUser,
    effectiveClient,
    effectiveRole,
    // Actions
    login,
    logout,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
